/**
 * StationStatusPreloader — cold-start refresh + 60 s polling loop.
 *
 * Extracted from Suggester (spec §3.1 rename: `SuggesterStatusPreloader` →
 * `StationStatusPreloader`). Semantics are unchanged; both Suggester and
 * StationCard share this preloader via the host (app root owns one instance).
 *
 * Cross-platform contract (spec §8 / §5):
 * - Cadence: 60 000 ms STRICT constant.
 * - `refresh()`: single in-flight (concurrent calls coalesce onto the same
 *   promise, not a new fetch). ETag / 304 handled.
 * - `start()` / `stop()`: interval management. Paused when page is hidden
 *   (the host wires `visibilitychange`), resumed on foreground.
 *
 * Mirrors iOS `StationStatusPreloader` and Android `StationStatusPreloader`.
 */
import type { StationStatusCache } from './cache'
import type { StationStatusClient } from './client'

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Preloader interface (domain-agnostic — no references to host, schema, or UI).
 * Mirrors iOS `StationStatusPreloader` protocol.
 */
export interface StationStatusPreloader {
  /**
   * Fetch the latest snapshot once. Concurrent calls while a fetch is
   * already in-flight coalesce onto the same promise — no duplicate requests.
   * Uses the cached ETag for conditional GET (304 = no-op).
   */
  refresh(): Promise<void>
  /**
   * Start the 60 s polling loop. Each cycle: refresh() → wait 60 s → repeat.
   * Calling `start()` while already running is a no-op.
   */
  start(): void
  /**
   * Stop the 60 s polling loop. Any in-flight `refresh()` completes normally;
   * the loop just doesn't restart. Calling `stop()` when not running is a no-op.
   */
  stop(): void
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Polling interval in milliseconds.
 * STRICT cross-platform constant (spec §5 / §8): iOS `refreshIntervalSeconds = 60`,
 * Android `REFRESH_INTERVAL_MS = 60_000`.
 */
export const REFRESH_INTERVAL_MS = 60_000

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

interface PreloaderDeps {
  client: StationStatusClient
  cache: StationStatusCache
  /**
   * Optional health hook (domain-agnostic — the package stays unaware of the
   * analytics backend). Invoked when a refresh fails and the preloader keeps
   * serving the STALE cache (the silent fallback). The host wires this to its
   * app-health reporter (`api_request_failed`, `fallback=cache`); omitted in
   * tests / harnesses. NEVER allowed to throw into the loop — the host wraps it.
   */
  onFetchError?: (error: Error) => void
}

/**
 * Create a `StationStatusPreloader` for the given client/cache pair.
 *
 * The host instantiates one preloader at app root, calls `refresh()` on cold
 * start (and on `visibilitychange` to `visible`), and `start()` / `stop()`
 * to manage the 60 s loop.
 */
export function createStationStatusPreloader({
  client,
  cache,
  onFetchError,
}: PreloaderDeps): StationStatusPreloader {
  // The current in-flight refresh promise (for coalescing concurrent calls).
  let inFlight: Promise<void> | null = null
  let intervalId: ReturnType<typeof setInterval> | null = null

  const refresh = (): Promise<void> => {
    // Coalesce: if a refresh is already in flight, return the same promise.
    if (inFlight !== null) return inFlight

    const doRefresh = async (): Promise<void> => {
      try {
        const etag = await cache.etag()
        const result = await client.fetchStatuses(etag)
        if (result.kind === 'changed') {
          await cache.replace(result.data, result.etag)
        } else if (result.kind === 'error') {
          // `error` → swallowed for the UI (spec §5: errors are silent, the
          // 60 s loop retries naturally), but surfaced to the OPTIONAL health
          // hook: we are now serving the STALE cache (the silent fallback the
          // health contract §4 wants visible). Wrapped so a misbehaving hook
          // can never break the loop.
          try {
            onFetchError?.(result.error)
          } catch {
            // health hook threw — never let it propagate into the refresh loop.
          }
        }
        // `notModified` → cache is fresh, no-op.
      } catch {
        // Belt-and-suspenders: any unexpected throw is silently swallowed so
        // the interval keeps running.
      } finally {
        inFlight = null
      }
    }

    inFlight = doRefresh()
    return inFlight
  }

  return {
    refresh,

    start() {
      if (intervalId !== null) return // already running
      intervalId = setInterval(() => {
        void refresh()
      }, REFRESH_INTERVAL_MS)
    },

    stop() {
      if (intervalId === null) return
      clearInterval(intervalId)
      intervalId = null
    },
  }
}
